"use client";

import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { AnimatedCounter } from "./animated-counter";
import { estimateDisplayTokenCount } from "./text-metrics";

/** Map of internal tool identifiers to user-friendly names */
const TOOL_NAME_MAP: Record<string, string> = {
  sandbox_full_file_rewrite: "file rewrite tool",
  sandbox_str_replace: "text replacement tool",
  sandbox_create_file: "file creation tool",
  sandbox_write_file: "file write tool",
  sandbox_read_file: "file read tool",
  sandbox_delete_file: "file delete tool",
  sandbox_execute_command: "command execution tool",
  sandbox_generate_pdf: "PDF generation tool",
  sandbox_check_command_output: "command output check tool",
  sandbox_list_files: "file listing tool",
  sandbox_list_commands: "command listing tool",
  sandbox_cleanup: "cleanup tool",
  search_web: "web search",
  search_knowledge: "knowledge search",
  generate_image: "image generation tool",
};

/** Escapes a literal string so it can be safely used inside a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Inline code is HTML/CSS-shaped (and therefore noise for non-developer users). */
const HTML_LIKE_INLINE_CODE = /[<>]|=\s*["']|:\s*[^;]+;|\{[^}]*\}/;

/**
 * Replaces internal tool identifiers with user-friendly names in reasoning text
 * and strips implementation markup (fenced code blocks + HTML/CSS-shaped inline
 * code) the model emits while debugging an artifact. End users see the
 * reasoning trace as "what the agent is thinking" — they don't want to see
 * `<style>` tags or `style="width: 25%;"` attributes leak through. The
 * non-implementation prose around the snippet is preserved.
 *
 * Presentation-layer filter only — does not modify stored data.
 */
function sanitizeReasoning(text: string): string {
  // Strip fenced code blocks (```lang\n...\n```). Reasoning is meant to be
  // human-readable narration of the model's plan, not a paste of the HTML or
  // CSS it's about to write. Anything in a fenced block is implementation
  // detail the artifact canvas will render anyway.
  let sanitized = text.replace(/```[\s\S]*?```/g, "");

  // Strip inline code spans that look like raw HTML/CSS (tags, attributes,
  // or CSS declarations). Keep simple inline code such as `425` or `column
  // name` so the reasoning remains scannable.
  sanitized = sanitized.replace(/`([^`\n]{1,200})`/g, (match, inner) =>
    HTML_LIKE_INLINE_CODE.test(inner) ? "" : match,
  );

  // Collapse runs of whitespace the stripping above may have left behind
  // (e.g., "the width is  but the text says…"). We only collapse spaces
  // inside lines so paragraph breaks survive.
  sanitized = sanitized.replace(/[ \t]{2,}/g, " ").replace(/ +([,.;:!?])/g, "$1");

  // Replace "functions.tool_name" patterns
  sanitized = sanitized.replace(/functions\.(\w+)/g, (_, toolName) => {
    return TOOL_NAME_MAP[toolName] || toolName.replace(/_/g, " ");
  });

  // Replace standalone tool name identifiers (regex form so we don't depend
  // on String.replaceAll / es2021)
  for (const [toolId, friendlyName] of Object.entries(TOOL_NAME_MAP)) {
    sanitized = sanitized.replace(new RegExp(escapeRegExp(toolId), "g"), friendlyName);
  }

  return sanitized;
}

/** Compact live reasoning status shown while the details remain collapsed. */
function ReasoningSummary({
  isStreaming,
  duration,
  tokenCount,
}: {
  isStreaming: boolean;
  duration?: number;
  tokenCount: number;
}) {
  if (!isStreaming) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <span>
          {duration === undefined
            ? "Thought for a few seconds"
            : `Thought for ${duration}s`}
        </span>
        <span className="text-muted-foreground/60">|</span>
        <AnimatedCounter value={tokenCount} />
        <span>tokens</span>
      </span>
    );
  }

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Shimmer duration={1}>Thinking...</Shimmer>
      <span>{duration ?? 0}s</span>
      <span className="text-muted-foreground/60">|</span>
      <AnimatedCounter value={tokenCount} />
      <span>tokens</span>
    </span>
  );
}

/**
 * Props for the MessageReasoning component
 */
type MessageReasoningProps = {
  /** Whether the reasoning is currently being loaded/generated */
  isLoading: boolean;
  /** The reasoning content to display, supports markdown formatting */
  reasoning: string;
  /** Actual provider-reported reasoning tokens when available after completion */
  reasoningTokens?: number;
};

/**
 * MessageReasoning - A collapsible component that displays AI reasoning information
 *
 * This component uses AI Elements Reasoning component which provides:
 * - Animated expand/collapse with smooth transitions
 * - Auto-close after streaming completes (with 1s delay)
 * - Duration tracking for how long the AI was thinking
 * - Shimmer loading state during streaming
 * - Streamdown markdown rendering for content
 *
 * @param props - The component props
 * @param props.isLoading - Controls whether to show loading state or completed state
 * @param props.reasoning - The reasoning text content (markdown supported)
 * @returns JSX element containing the reasoning display
 */
export function MessageReasoning({
  isLoading,
  reasoning,
  reasoningTokens,
}: MessageReasoningProps) {
  // Hide reasoning blocks that are empty or only contain [REDACTED]
  // Still show during streaming so the user sees "Thinking..." indicator
  const trimmed = reasoning.trim();
  if (!isLoading && (trimmed.length === 0 || trimmed === "[REDACTED]")) {
    return null;
  }
  const sanitizedReasoning = sanitizeReasoning(reasoning);
  const tokenCount =
    !isLoading && typeof reasoningTokens === "number"
      ? reasoningTokens
      : estimateDisplayTokenCount(sanitizedReasoning);

  return (
    <div className="flex flex-col">
      <Reasoning
        isStreaming={isLoading}
        defaultOpen={false}
        className="!my-2"
      >
        <ReasoningTrigger
          data-testid="message-reasoning-toggle"
          getThinkingMessage={(isStreaming, duration) => (
            <ReasoningSummary
              isStreaming={isStreaming}
              duration={duration}
              tokenCount={tokenCount}
            />
          )}
        />
        <ReasoningContent
          data-testid="message-reasoning"
          className="pl-4 border-l-2 border-gray-200 dark:border-gray-700 text-sm"
        >
          {sanitizedReasoning}
        </ReasoningContent>
      </Reasoning>
    </div>
  );
}
